import { handle } from 'redux-pack';
import { logFailure } from 'utils/log';
import { fromApiToStore } from 'models/mapping/Checklist';
import assign from 'assign-deep';
import {
  cloneFixedHouseAddressItems,
  ensureFixedHouseItemsShape,
  mergeFixedHouseAddressItems,
} from './utils/houseAddressMerge';

const getFinishSessionHttpStatus = payload =>
  payload && payload.response && payload.response.status;

const getFinishSessionErrorDetails = (payload = {}) => {
  const details = payload.details;
  const error = details && details.error;
  const errorMessage = error && error.message;
  const mapaErros =
    (errorMessage && errorMessage.mapaErros) ||
    (error && error.mapaErros) ||
    (details && details.mapaErros) ||
    (payload.error && payload.error.mapaErros);

  return {
    httpStatus: getFinishSessionHttpStatus(payload),
    mapaErros,
    details,
    message: payload.message,
    name: payload.name,
  };
};

export const handleStartPhotoSessionLifecycle = action => ({
  success: prevState => prevState,
  failure: prevState => {
    logFailure({
      message: '[Failure when start photo session]',
      payload: action.payload,
      state: prevState,
    });
    return {
      ...prevState,
      showError: true,
    };
  },
});

export const startPhotoSession = (state, action) =>
  handle(state, action, handleStartPhotoSessionLifecycle(action));

export const updateChecklistSectionItem = (
  _state,
  { sectionType, section, itemSlug, value, previousValue },
  rollback = false
) => {
  const state = _state;

  if (
    state[sectionType] &&
    state[sectionType][section] &&
    state[sectionType][section].items
  ) {
    const isHouseComplementEdit =
      sectionType === 'fixed' && section === 'house' && itemSlug === 'complement';

    state[sectionType][section].items[itemSlug] = {
      ...state[sectionType][section].items[itemSlug],
      value: rollback ? previousValue : value,
      ...(isHouseComplementEdit && !rollback ? { editedByUser: true } : {}),
    };
  }

  return state;
};

export const handleSaveChecklistItemInStorageLifecycle = action => ({
  failure: prevState => {
    logFailure({
      message: '[Failure when saving an item in storage]',
      payload: action.payload,
      state: prevState,
    });

    return updateChecklistSectionItem(prevState, action.meta, true);
  },
});

export const saveChecklistItemInStorage = (state, action) =>
  handle(state, action, handleSaveChecklistItemInStorageLifecycle(action));

export const getChecklistItems = ({ prevState, action }) => {
  const state = prevState;

  assign(state, {
    hasFetchedChecklistItems: true,
    ...fromApiToStore(action.payload),
  });

  if (state.fixed?.house?.items) {
    state.fixed.house.items = ensureFixedHouseItemsShape(
      state.fixed.house.items,
    );
  }

  return state;
};

export const handleGetChecklistItemsLifecycle = action => ({
  success: prevState => getChecklistItems({ prevState, action }),
  failure: prevState => {
    logFailure({
      message: '[Failure fetching checklist items]',
      payload: action.payload,
      state: prevState,
    });
    return prevState;
  },
});

export const handleGetChecklistItems = (state, action) =>
  handle(state, action, handleGetChecklistItemsLifecycle(action));

export const getChecklistItemsFromStorage = (prevState, action) => {
  const state = prevState;
  const previousHouseItems = cloneFixedHouseAddressItems(
    state.fixed?.house?.items,
  );

  assign(state, {
    hasFetchedChecklistItemsFromStorage: true,
    isFetchingChecklistFromStorage: false,
    ...action.payload,
  });

  if (state.fixed?.house?.items && previousHouseItems) {
    state.fixed.house.items = mergeFixedHouseAddressItems(
      previousHouseItems,
      state.fixed.house.items,
    );
  }

  if (state.fixed?.house?.items) {
    state.fixed.house.items = ensureFixedHouseItemsShape(
      state.fixed.house.items,
    );
  }

  return state;
};

export const handleGetChecklistItemsFromStorageLifecycle = action => ({
  start: prevState => ({
    ...prevState,
    isFetchingChecklistFromStorage: true,
  }),
  success: prevState => getChecklistItemsFromStorage(prevState, action),
  failure: prevState => {
    logFailure({
      message: '[Failure fetching checklist items from storage]',
      payload: action.payload,
      state: prevState,
    });

    return {
      ...prevState,
      isFetchingChecklistFromStorage: false,
    };
  },
});

export const handleGetChecklistItemsFromStorage = (state, action) =>
  handle(state, action, handleGetChecklistItemsFromStorageLifecycle(action));

export const updateDialogLoadingState = prevState => ({
  ...prevState,
  isDialogLoading: true,
});

export const finishChecklistSession = (prevState, action, error) => {
  if (error) {
    const statusCodes = getFinishSessionHttpStatus(action.payload);

    return statusCodes === 403
      ? {
        ...prevState,
        isDialogLoading: false,
      }
      : {
        ...prevState,
        hasFinishSessionError: true,
        isDialogLoading: false,

        finishSessionError:
            action.payload.details && action.payload.details.error,
      };
  }

  return {
    ...prevState,
    isSessionFinished: true,
    isDialogLoading: false,
  };
};

export const handleChecklistSessionFinishLifecycle = action => ({
  start: prevState => updateDialogLoadingState(prevState),
  success: prevState => finishChecklistSession(prevState, action, false),
  failure: prevState => {
    const httpStatus = getFinishSessionHttpStatus(action.payload);

    if (httpStatus !== 403) {
      logFailure({
        message: '[Failure finish checklist]',
        payload: getFinishSessionErrorDetails(action.payload),
        state: prevState,
      });
    }

    return finishChecklistSession(prevState, action, true);
  },
});

export const handleChecklistSessionFinish = (state, action) =>
  handle(state, action, handleChecklistSessionFinishLifecycle(action));

export const handleUpdateIsChecklistValid = (state, action) => ({
  ...state,
  isChecklistValid: action.extraProperties.isValid,
});

export const handleUpdateShouldOpenDialog = (state, action) => ({
  ...state,
  shouldOpenDialog: action.extraProperties.shouldOpenDialog,
});

export const handleOpenFinishSessionDialog = state => ({
  ...state,
  isChecklistValid: true,
  shouldOpenDialog: true,
});
